// =====================================================================
// NotificationChannel zod mirror (Sprint 3 Phase 3G-v, 2026-05-31)
// =====================================================================
// Mirror of the Rust `NotificationChannel` model in
// `crates/rhia-protocol/src/alerts.rs`. Workspace-scoped so multiple
// projects can share a single channel.
// =====================================================================

import { z } from 'zod';

export const SmtpEmailConfigSchema = z.object({
  kind: z.literal('smtp_email'),
  recipients: z.array(z.string().min(1)).min(1, 'at least one recipient'),
  smtp_host: z.string().nullable(),
  smtp_port: z.number().int().min(1).max(65535).nullable(),
  smtp_user: z.string().nullable(),
  smtp_pass: z.string().nullable(),
  from_address: z.string().nullable(),
  require_tls: z.boolean(),
});

export const WebhookConfigSchema = z.object({
  kind: z.literal('webhook'),
  url: z.string().url(),
  // Allow empty so the redacted form the server returns on reads
  // (signing_secret stripped to '') still parses. The CRUD route
  // enforces "non-empty on create" via preserveSecrets logic.
  signing_secret: z.string(),
  extra_headers: z.array(z.tuple([z.string().min(1), z.string()])),
});

export const NotificationChannelConfigSchema = z.discriminatedUnion('kind', [
  SmtpEmailConfigSchema,
  WebhookConfigSchema,
]);
export type ParsedNotificationChannelConfig = z.infer<typeof NotificationChannelConfigSchema>;

export const NotificationChannelSchema = z.object({
  id: z.string().min(1),
  workspace_id: z.string().min(1),
  name: z.string().min(1),
  config: NotificationChannelConfigSchema,
  enabled: z.boolean(),
  archived_at_ms: z.number().int().nullable(),
  created_at_ms: z.number().int().nonnegative(),
  updated_at_ms: z.number().int().nonnegative(),
});
export type ParsedNotificationChannel = z.infer<typeof NotificationChannelSchema>;

export const CreateNotificationChannelSchema = z.object({
  workspace_id: z.string().min(1),
  name: z.string().min(1),
  config: NotificationChannelConfigSchema,
  enabled: z.boolean(),
});
export type ParsedCreateNotificationChannel = z.infer<typeof CreateNotificationChannelSchema>;

export const UpdateNotificationChannelSchema = z.object({
  name: z.string().min(1).optional(),
  config: NotificationChannelConfigSchema.optional(),
  enabled: z.boolean().optional(),
});
export type ParsedUpdateNotificationChannel = z.infer<typeof UpdateNotificationChannelSchema>;
